import { useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { startMatch } from "@/client/main";
import { useAppStore } from "@/store/useAppStore";
import { getRoom, leaveSession } from "@/client/session";

export const Route = createFileRoute("/play")({
  component: Play,
});

function Play() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const matchEnded = useAppStore((state) => state.matchEnded);
  const roomId = useAppStore((state) => state.roomId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!canvasRef.current) return;

    // La Room ne naît plus ici : elle vient du lobby et lui survit. Arriver
    // sur /play sans session (rechargement, accès direct) n'est donc pas une
    // Partie à démarrer, c'est un retour à l'accueil.
    const room = getRoom();
    if (!room) {
      navigate({ to: "/" });
      return;
    }

    // Une nouvelle Partie repart toujours avec le flag à son état initial —
    // sinon un flag laissé à true par la précédente renaviguerait
    // immédiatement hors de /play au remount.
    useAppStore.getState().resetMatch();

    const cleanup = startMatch(canvasRef.current, room);
    return () => {
      cleanup();
      // Quitter /play, c'est quitter la Room : elle est verrouillée depuis le
      // décompte, personne ne peut plus y entrer, elle n'a plus d'usage.
      void leaveSession();
    };
    // Intentionally run once on mount: startMatch bootstraps the KAPLAY
    // instance over an already-connected Room.
  }, []);

  useEffect(() => {
    // KAPLAY ne fait jamais navigate()/go() lui-même : il se contente de
    // signaler la fin de Partie via endMatch(), c'est React qui décide ici
    // de démonter (déclenchant le cleanup -> k.quit()) et de renaviguer.
    if (matchEnded) navigate({ to: "/" });
  }, [matchEnded, navigate]);

  useEffect(() => {
    // Lien perdu en cours de Partie (serveur tombé, Room disposée) : `session`
    // remet roomId à null. Un canvas figé n'est pas un état.
    if (roomId === null) navigate({ to: "/" });
  }, [roomId, navigate]);

  return <canvas ref={canvasRef} />;
}
